import { useRef, useState } from 'react';
import { Box, FormHelperText, Stack, TextField, Typography } from '@mui/material';

import type { DesktopNotificationPlaceholderDto } from '@/api/types/desktopNotification.types';
import { extractPlaceholders, PLACEHOLDER_REGEX } from '@/utils/placeholders';
import { PlaceholderChips } from './PlaceholderChips';

interface ReminderBodyEditorProps {
  header: string;
  body: string;
  allowedTokens: DesktopNotificationPlaceholderDto[];
  onChange: (next: { header: string; body: string }) => void;
}

type FocusedField = 'header' | 'body';

const PREVIEW_SAMPLES: Record<string, string> = {
  FirstName: 'Alex',
  FullName: 'Alex Doe',
  PreferredName: 'Alex',
  Office: 'London',
  BillingGrade: 'AC',
  WeekEnding: '31 May 2026',
  SurveyDueDate: '06 Jun 2026',
  DaysUntilDue: '2',
  SurveyLink: 'https://qualtrics.example/survey/...',
  TodayDate: '30 May 2026',
};

const SURVEY_LINK_REGEX = /\{\{\s*SurveyLink\s*\}\}/;
const SURVEY_LINK_LABEL = 'TAKE SURVEY';

function substitutePlaceholders(text: string): string {
  const regex = new RegExp(PLACEHOLDER_REGEX.source, PLACEHOLDER_REGEX.flags);
  return text.replace(regex, (match, token) => {
    return PREVIEW_SAMPLES[token] ?? match;
  });
}

// The desktop pop-up shows the survey link as a button, never the URL, so the preview does too.
function renderBodyPreview(body: string): React.ReactNode[] {
  return body
    .split(SURVEY_LINK_REGEX)
    .flatMap((part, index) =>
      index === 0
        ? [substitutePlaceholders(part)]
        : [<SurveyLinkPreview key={index} />, substitutePlaceholders(part)],
    );
}

function SurveyLinkPreview() {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        typography: 'button',
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        borderRadius: 1,
        px: 2,
        py: 0.5,
      }}
    >
      {SURVEY_LINK_LABEL}
    </Box>
  );
}

function insertAtCursor(
  input: HTMLInputElement | HTMLTextAreaElement | null,
  value: string,
  token: string,
): { value: string; nextSelection: number } {
  const insertion = `{{ ${token} }}`;
  if (!input) {
    return { value: value + insertion, nextSelection: value.length + insertion.length };
  }
  const start = input.selectionStart ?? value.length;
  const end = input.selectionEnd ?? value.length;
  const next = value.slice(0, start) + insertion + value.slice(end);
  return { value: next, nextSelection: start + insertion.length };
}

export function ReminderBodyEditor({
  header,
  body,
  allowedTokens,
  onChange,
}: ReminderBodyEditorProps) {
  const headerRef = useRef<HTMLInputElement | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const [focusedField, setFocusedField] = useState<FocusedField>('body');

  const handleHeaderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ header: e.target.value, body });
  };

  const handleBodyChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChange({ header, body: e.target.value });
  };

  const handleInsertToken = (token: string) => {
    if (focusedField === 'header') {
      const { value, nextSelection } = insertAtCursor(headerRef.current, header, token);
      onChange({ header: value, body });
      requestAnimationFrame(() => {
        headerRef.current?.focus();
        headerRef.current?.setSelectionRange(nextSelection, nextSelection);
      });
    } else {
      const { value, nextSelection } = insertAtCursor(bodyRef.current, body, token);
      onChange({ header, body: value });
      requestAnimationFrame(() => {
        bodyRef.current?.focus();
        bodyRef.current?.setSelectionRange(nextSelection, nextSelection);
      });
    }
  };

  const allowedTokenNames = allowedTokens.map((t) => t.token);
  const usedTokens = Array.from(
    new Set([...extractPlaceholders(header), ...extractPlaceholders(body)]),
  );
  const unknownTokens = usedTokens.filter((t) => !allowedTokenNames.includes(t));

  return (
    <Stack spacing={2}>
      <TextField
        label="Header"
        size="small"
        fullWidth
        value={header}
        onChange={handleHeaderChange}
        inputRef={headerRef}
        onFocus={() => setFocusedField('header')}
        inputProps={{ maxLength: 500, 'aria-label': 'Header' }}
      />

      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Placeholders (click to insert at cursor)
        </Typography>
        <PlaceholderChips tokens={allowedTokens} onInsert={handleInsertToken} />
      </Box>

      <TextField
        label="Body"
        size="small"
        fullWidth
        multiline
        rows={8}
        value={body}
        onChange={handleBodyChange}
        inputRef={bodyRef}
        onFocus={() => setFocusedField('body')}
        inputProps={{ maxLength: 4000, 'aria-label': 'Body' }}
      />

      {unknownTokens.length > 0 && (
        <FormHelperText error>
          Unknown placeholder(s): {unknownTokens.map((t) => `'${t}'`).join(', ')}.
        </FormHelperText>
      )}

      <Box sx={{ bgcolor: 'action.hover', p: 2, mt: 2 }} data-testid="reminder-preview">
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Preview
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {substitutePlaceholders(header)}
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {renderBodyPreview(body)}
        </Typography>
      </Box>
    </Stack>
  );
}

export default ReminderBodyEditor;
